import React, { useMemo, useState } from "react";
import {
    useGetAdminAltynBalanceQuery,
    useGetAdminAltynStatsQuery,
    useGetAdminAltynClientsQuery,
    useGetAdminAltynPaymentsQuery,
    useGetAdminAltynPaymentQuery,
    useGetAdminAltynPayoutsQuery,
    useGetAdminAltynPayoutQuery,
    useCheckAdminAltynPayoutMutation,
    useCreateAltynCryptoPayoutMutation,
} from "../api";

export const AdminAltynPage: React.FC = () => {
    const [activeTab, setActiveTab] = useState<"payments" | "clients" | "payouts">("payments");
    const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
    const [selectedPayoutId, setSelectedPayoutId] = useState<number | null>(null);

    // Balance
    const { data: balanceResponse, isLoading: isLoadingBalance, refetch: refetchBalance } =
        useGetAdminAltynBalanceQuery(undefined);

    // Stats
    const { data: statsResponse, isLoading: isLoadingStats, refetch: refetchStats } =
        useGetAdminAltynStatsQuery(undefined);

    // Payments filters
    const [paymentsPage, setPaymentsPage] = useState(1);
    const [paymentsUserId, setPaymentsUserId] = useState("");
    const [paymentsPhone, setPaymentsPhone] = useState("");
    const [paymentsStatusCode, setPaymentsStatusCode] = useState("");
    const [paymentsCredited, setPaymentsCredited] = useState<"" | "1" | "0">("");

    const paymentsParams = useMemo(() => {
        return {
            page: paymentsPage,
            per_page: 15,
            sort_by: "created_at" as const,
            sort_order: "desc" as const,
            user_id: paymentsUserId ? parseInt(paymentsUserId) : undefined,
            phone: paymentsPhone || undefined,
            status_code: paymentsStatusCode ? parseInt(paymentsStatusCode) : undefined,
            credited:
                paymentsCredited === "" ? undefined : paymentsCredited === "1",
        };
    }, [paymentsPage, paymentsUserId, paymentsPhone, paymentsStatusCode, paymentsCredited]);

    const {
        data: paymentsResponse,
        isLoading: isLoadingPayments,
        refetch: refetchPayments,
    } = useGetAdminAltynPaymentsQuery(paymentsParams);

    const {
        data: payoutDetailsResponse,
        isLoading: isLoadingPayoutDetails,
    } = useGetAdminAltynPayoutQuery(selectedPayoutId!, {
        skip: selectedPayoutId === null,
    });

    const payoutDetails = payoutDetailsResponse?.data;

    // Clients filters
    const [clientsPage, setClientsPage] = useState(1);
    const [clientsUserId, setClientsUserId] = useState("");
    const [clientsPhone, setClientsPhone] = useState("");
    const [clientsIsBlocked, setClientsIsBlocked] = useState<"" | "1" | "0">("");

    const clientsParams = useMemo(() => {
        return {
            page: clientsPage,
            per_page: 15,
            sort_by: "created_at" as const,
            sort_order: "desc" as const,
            user_id: clientsUserId ? parseInt(clientsUserId) : undefined,
            phone: clientsPhone || undefined,
            is_blocked:
                clientsIsBlocked === "" ? undefined : clientsIsBlocked === "1",
        };
    }, [clientsPage, clientsUserId, clientsPhone, clientsIsBlocked]);

    const {
        data: clientsResponse,
        isLoading: isLoadingClients,
        refetch: refetchClients,
    } = useGetAdminAltynClientsQuery(clientsParams);

    const enabled = balanceResponse?.data?.enabled ?? true;
    const merchantBalance = balanceResponse?.data?.balance ?? null;

    const payments = paymentsResponse?.data ?? [];
    const paymentsMeta = paymentsResponse?.meta;

    const clients = clientsResponse?.data ?? [];
    const clientsMeta = clientsResponse?.meta;

    // Payouts filters
    const [payoutsPage, setPayoutsPage] = useState(1);
    const [payoutsUserId, setPayoutsUserId] = useState("");
    const [payoutsPhone, setPayoutsPhone] = useState("");
    const [payoutsStatusCode, setPayoutsStatusCode] = useState("");
    const [payoutsCurrency, setPayoutsCurrency] = useState("");

    const payoutsParams = useMemo(() => {
        return {
            page: payoutsPage,
            per_page: 15,
            sort_by: "created_at" as const,
            sort_order: "desc" as const,
            user_id: payoutsUserId ? parseInt(payoutsUserId) : undefined,
            phone: payoutsPhone || undefined,
            status_code: payoutsStatusCode ? parseInt(payoutsStatusCode) : undefined,
            currency: payoutsCurrency || undefined,
        };
    }, [payoutsPage, payoutsUserId, payoutsPhone, payoutsStatusCode, payoutsCurrency]);

    const {
        data: payoutsResponse,
        isLoading: isLoadingPayouts,
        refetch: refetchPayouts,
    } = useGetAdminAltynPayoutsQuery(payoutsParams);

    const payouts = payoutsResponse?.data ?? [];
    const payoutsMeta = payoutsResponse?.meta;

    const [checkPayout, { isLoading: isCheckingPayout }] = useCheckAdminAltynPayoutMutation();
    const [createPayout, { isLoading: isCreatingPayout }] = useCreateAltynCryptoPayoutMutation();

    // Form state for creating payout
    const [payoutForm, setPayoutForm] = useState({
        amount: "",
        currency: "USDT",
        wallet_address: "",
        rate_id: "",
    });

    const handleCreatePayout = async () => {
        if (!payoutForm.amount || !payoutForm.wallet_address) {
            alert("Заполните сумму и адрес кошелька");
            return;
        }
        try {
            await createPayout({
                amount: payoutForm.amount,
                currency: payoutForm.currency,
                wallet_address: payoutForm.wallet_address,
                rate_id: payoutForm.rate_id || undefined,
            }).unwrap();
            alert("Выплата создана успешно");
            setPayoutForm({ amount: "", currency: "USDT", wallet_address: "", rate_id: "" });
            refetchPayouts();
        } catch (error: any) {
            alert(`Ошибка: ${error?.data?.message || error?.message || "Неизвестная ошибка"}`);
        }
    };

    const handleCheckPayout = async (id: number) => {
        try {
            await checkPayout(id).unwrap();
            refetchPayouts();
        } catch (error: any) {
            alert(`Ошибка проверки: ${error?.data?.message || error?.message || "Неизвестная ошибка"}`);
        }
    };

    // Payment details query
    const {
        data: paymentDetailsResponse,
        isLoading: isLoadingPaymentDetails,
    } = useGetAdminAltynPaymentQuery(selectedPaymentId!, {
        skip: selectedPaymentId === null,
    });

    const paymentDetails = paymentDetailsResponse?.data;

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Altyn
                    </h1>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Платежи, клиенты и баланс мерчанта
                    </p>
                </div>

                <button
                    onClick={() => {
                        refetchBalance();
                        refetchStats();
                        if (activeTab === "payments") {
                            refetchPayments();
                        } else if (activeTab === "clients") {
                            refetchClients();
                        } else {
                            refetchPayouts();
                        }
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    Обновить
                </button>
            </div>

            {/* Balance and Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Balance */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Баланс мерчанта
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {isLoadingBalance
                                ? "Загрузка..."
                                : enabled
                                  ? "Altyn включён"
                                  : "Altyn отключён"}
                        </div>
                    </div>

                    {!enabled && (
                        <div className="mt-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
                            Сервис Altyn выключен настройкой{" "}
                            <span className="font-mono">altyn_enabled</span>.
                            Баланс не запрашивается.
                        </div>
                    )}

                    {enabled && (
                        <div className="mt-4">
                            {merchantBalance ? (
                                <div className="text-gray-900 dark:text-white text-lg font-semibold">
                                    {merchantBalance.balance}{" "}
                                    {merchantBalance.currency}
                                </div>
                            ) : (
                                <div className="text-gray-500 dark:text-gray-400 text-sm">
                                    Баланс не получен
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Total Payments */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Общая сумма платежей
                    </div>
                    <div className="mt-4">
                        {isLoadingStats ? (
                            <div className="text-gray-500 dark:text-gray-400 text-sm">
                                Загрузка...
                            </div>
                        ) : (
                            <div className="text-gray-900 dark:text-white text-lg font-semibold">
                                {statsResponse?.data?.total_payments || "0"}{" "}
                                USDT
                            </div>
                        )}
                    </div>
                </div>

                {/* Total Payouts */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Общая сумма выплат
                    </div>
                    <div className="mt-4">
                        {isLoadingStats ? (
                            <div className="text-gray-500 dark:text-gray-400 text-sm">
                                Загрузка...
                            </div>
                        ) : (
                            <div className="text-gray-900 dark:text-white text-lg font-semibold">
                                {statsResponse?.data?.total_payouts || "0"} USDT
                            </div>
                        )}
                    </div>
                </div>

                {/* Net Balance */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Итоговый баланс
                    </div>
                    <div className="mt-4">
                        {isLoadingStats ? (
                            <div className="text-gray-500 dark:text-gray-400 text-sm">
                                Загрузка...
                            </div>
                        ) : (
                            <div
                                className={`text-lg font-semibold ${
                                    parseFloat(
                                        statsResponse?.data?.net_balance || "0",
                                    ) >= 0
                                        ? "text-green-600 dark:text-green-400"
                                        : "text-red-600 dark:text-red-400"
                                }`}
                            >
                                {statsResponse?.data?.net_balance || "0"} USDT
                            </div>
                        )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Пополнения - Выплаты
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2">
                <button
                    onClick={() => setActiveTab("payments")}
                    className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        activeTab === "payments"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                    Платежи
                </button>
                <button
                    onClick={() => setActiveTab("clients")}
                    className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        activeTab === "clients"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                    Клиенты
                </button>
                <button
                    onClick={() => setActiveTab("payouts")}
                    className={`px-4 py-2 text-sm font-medium rounded-md border ${
                        activeTab === "payouts"
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                >
                    Выплаты
                </button>
            </div>

            {activeTab === "payments" && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    ID пользователя
                                </label>
                                <input
                                    type="number"
                                    value={paymentsUserId}
                                    onChange={(e) => {
                                        setPaymentsUserId(e.target.value);
                                        setPaymentsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="Например 123"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Телефон (user.mobile)
                                </label>
                                <input
                                    type="text"
                                    value={paymentsPhone}
                                    onChange={(e) => {
                                        setPaymentsPhone(e.target.value);
                                        setPaymentsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="+7999 или часть"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status code
                                </label>
                                <input
                                    type="number"
                                    value={paymentsStatusCode}
                                    onChange={(e) => {
                                        setPaymentsStatusCode(e.target.value);
                                        setPaymentsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="Например 2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Зачисление
                                </label>
                                <select
                                    value={paymentsCredited}
                                    onChange={(e) => {
                                        setPaymentsCredited(
                                            e.target.value as any,
                                        );
                                        setPaymentsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Все</option>
                                    <option value="1">Зачислено</option>
                                    <option value="0">Не зачислено</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Пользователь
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Сумма (RUB)
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            USDT
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Статус
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Создано
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {isLoadingPayments ? (
                                        <tr>
                                            <td
                                                colSpan={7}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Загрузка...
                                            </td>
                                        </tr>
                                    ) : payments.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={7}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Платежи не найдены
                                            </td>
                                        </tr>
                                    ) : (
                                        payments.map((p) => (
                                            <tr
                                                key={p.id}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {p.id}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    <div className="font-medium">
                                                        {p.user?.surname}{" "}
                                                        {p.user?.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        ID {p.user_id} ·{" "}
                                                        {p.user?.mobile || "—"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {p.amount_rub}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {p.amount_usdt ?? "—"}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    <div>
                                                        code:{" "}
                                                        {p.status_code ?? "—"}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {p.status_text || ""}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {p.created_at
                                                        ? new Date(
                                                              p.created_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </td>
                                                <td className="px-4 py-3 text-sm">
                                                    <button
                                                        onClick={() =>
                                                            setSelectedPaymentId(
                                                                p.id,
                                                            )
                                                        }
                                                        className="px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                                                    >
                                                        Детали
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {paymentsMeta && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Показано {paymentsMeta.from ?? 0}–
                                    {paymentsMeta.to ?? 0} из{" "}
                                    {paymentsMeta.total}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        disabled={
                                            paymentsMeta.current_page <= 1
                                        }
                                        onClick={() =>
                                            setPaymentsPage((p) =>
                                                Math.max(1, p - 1),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Назад
                                    </button>
                                    <button
                                        disabled={
                                            paymentsMeta.current_page >=
                                            paymentsMeta.last_page
                                        }
                                        onClick={() =>
                                            setPaymentsPage((p) =>
                                                Math.min(
                                                    paymentsMeta.last_page,
                                                    p + 1,
                                                ),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Вперёд
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === "clients" && (
                <div className="space-y-4">
                    {/* Filters */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    ID пользователя
                                </label>
                                <input
                                    type="number"
                                    value={clientsUserId}
                                    onChange={(e) => {
                                        setClientsUserId(e.target.value);
                                        setClientsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Телефон (Altyn)
                                </label>
                                <input
                                    type="text"
                                    value={clientsPhone}
                                    onChange={(e) => {
                                        setClientsPhone(e.target.value);
                                        setClientsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Блокировка
                                </label>
                                <select
                                    value={clientsIsBlocked}
                                    onChange={(e) => {
                                        setClientsIsBlocked(
                                            e.target.value as any,
                                        );
                                        setClientsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="">Все</option>
                                    <option value="0">Не заблокирован</option>
                                    <option value="1">Заблокирован</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Пользователь
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Телефон
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Account
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Уровень / блок
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {isLoadingClients ? (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Загрузка...
                                            </td>
                                        </tr>
                                    ) : clients.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Клиенты не найдены
                                            </td>
                                        </tr>
                                    ) : (
                                        clients.map((c) => (
                                            <tr
                                                key={c.id}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {c.id}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    <div className="font-medium">
                                                        {c.user?.surname}{" "}
                                                        {c.user?.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        ID {c.user_id} ·{" "}
                                                        {c.user?.mobile || "—"}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {c.phone}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    {c.account_number || "—"}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    lvl {c.identification_level}{" "}
                                                    ·{" "}
                                                    {c.is_blocked
                                                        ? "blocked"
                                                        : "ok"}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {clientsMeta && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Показано {clientsMeta.from ?? 0}–
                                    {clientsMeta.to ?? 0} из {clientsMeta.total}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        disabled={clientsMeta.current_page <= 1}
                                        onClick={() =>
                                            setClientsPage((p) =>
                                                Math.max(1, p - 1),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Назад
                                    </button>
                                    <button
                                        disabled={
                                            clientsMeta.current_page >=
                                            clientsMeta.last_page
                                        }
                                        onClick={() =>
                                            setClientsPage((p) =>
                                                Math.min(
                                                    clientsMeta.last_page,
                                                    p + 1,
                                                ),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Вперёд
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Details Modal */}
            {selectedPaymentId !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Детали платежа #{selectedPaymentId}
                            </h2>
                            <button
                                onClick={() => setSelectedPaymentId(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="overflow-y-auto p-6 flex-1">
                            {isLoadingPaymentDetails ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    Загрузка...
                                </div>
                            ) : paymentDetails ? (
                                <div className="space-y-6">
                                    {/* Основная информация */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Основная информация
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    User ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.user_id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    External ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {paymentDetails.external_id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Altyn ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {paymentDetails.altyn_id ||
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Method
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.method}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Bank
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.bank || "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Account Number
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {
                                                        paymentDetails.account_number
                                                    }
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Bank ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {paymentDetails.bank_id ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Суммы и статус */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Суммы и статус
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Amount RUB
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-semibold">
                                                    {paymentDetails.amount_rub}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Amount USDT
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-semibold">
                                                    {paymentDetails.amount_usdt ??
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Rate
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.rate ?? "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Status Code
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.status_code ??
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Status Text
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.status_text ||
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Credited At
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.credited_at
                                                        ? new Date(
                                                              paymentDetails.credited_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Credited Amount USDT
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.credited_amount_usdt ??
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ссылки */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Ссылки
                                        </h3>
                                        <div className="space-y-2">
                                            {paymentDetails.qr_url && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        QR URL
                                                    </label>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400 break-all">
                                                        <a
                                                            href={
                                                                paymentDetails.qr_url
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="hover:underline"
                                                        >
                                                            {
                                                                paymentDetails.qr_url
                                                            }
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                            {paymentDetails.deep_link && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Deep Link
                                                    </label>
                                                    <div className="text-sm text-blue-600 dark:text-blue-400 break-all">
                                                        <a
                                                            href={
                                                                paymentDetails.deep_link
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="hover:underline"
                                                        >
                                                            {
                                                                paymentDetails.deep_link
                                                            }
                                                        </a>
                                                    </div>
                                                </div>
                                            )}
                                            {paymentDetails.expires_at && (
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Expires At
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {new Date(
                                                            paymentDetails.expires_at,
                                                        ).toLocaleString(
                                                            "ru-RU",
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Пользователь */}
                                    {paymentDetails.user && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Пользователь
                                            </h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Имя
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {
                                                            paymentDetails.user
                                                                .surname
                                                        }{" "}
                                                        {
                                                            paymentDetails.user
                                                                .name
                                                        }
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Email
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {paymentDetails.user
                                                            .email || "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Телефон
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {paymentDetails.user
                                                            .mobile || "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Telegram
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {paymentDetails.user
                                                            .telegram_username ||
                                                            "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Страна
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {paymentDetails.user
                                                            .country || "—"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Processing Data */}
                                    {paymentDetails.processing_data && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Processing Data
                                            </h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(
                                                    paymentDetails.processing_data,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Raw Webhook */}
                                    {paymentDetails.raw_webhook && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Raw Webhook
                                            </h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(
                                                    paymentDetails.raw_webhook,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Даты */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Даты
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Created At
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.created_at
                                                        ? new Date(
                                                              paymentDetails.created_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Updated At
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {paymentDetails.updated_at
                                                        ? new Date(
                                                              paymentDetails.updated_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-red-500 dark:text-red-400">
                                    Не удалось загрузить детали платежа
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setSelectedPaymentId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "payouts" && (
                <div className="space-y-4">
                    {/* Create Payout Form */}
                    <div className="bg-blue-100 dark:bg-blue-900 shadow rounded-lg p-4">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Создать выплату криптовалютой
                        </h2>
                        <div className="text-sm text-red-600 dark:text-red-300 mb-4">Фиксированная комиссия за вывод 3 USDT</div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Сумма
                                </label>
                                <input
                                    type="text"
                                    value={payoutForm.amount}
                                    onChange={(e) =>
                                        setPayoutForm({
                                            ...payoutForm,
                                            amount: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="10.50"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Валюта
                                </label>
                                <input
                                    type="text"
                                    value={payoutForm.currency}
                                    onChange={(e) =>
                                        setPayoutForm({
                                            ...payoutForm,
                                            currency: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="USDT"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Адрес кошелька
                                </label>
                                <input
                                    type="text"
                                    value={payoutForm.wallet_address}
                                    onChange={(e) =>
                                        setPayoutForm({
                                            ...payoutForm,
                                            wallet_address: e.target.value,
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="0x..."
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    onClick={handleCreatePayout}
                                    disabled={isCreatingPayout}
                                    className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                                >
                                    {isCreatingPayout
                                        ? "Создание..."
                                        : "Создать"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    ID пользователя
                                </label>
                                <input
                                    type="number"
                                    value={payoutsUserId}
                                    onChange={(e) => {
                                        setPayoutsUserId(e.target.value);
                                        setPayoutsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="Например 123"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Телефон
                                </label>
                                <input
                                    type="text"
                                    value={payoutsPhone}
                                    onChange={(e) => {
                                        setPayoutsPhone(e.target.value);
                                        setPayoutsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="+7999 или часть"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Status code
                                </label>
                                <input
                                    type="number"
                                    value={payoutsStatusCode}
                                    onChange={(e) => {
                                        setPayoutsStatusCode(e.target.value);
                                        setPayoutsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="Например 2"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Валюта
                                </label>
                                <input
                                    type="text"
                                    value={payoutsCurrency}
                                    onChange={(e) => {
                                        setPayoutsCurrency(e.target.value);
                                        setPayoutsPage(1);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
                                    placeholder="USDT"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Пользователь
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Сумма USDT
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Сумма RUB
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Курс
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Кошелек
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Статус
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Создано
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                    {isLoadingPayouts ? (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Загрузка...
                                            </td>
                                        </tr>
                                    ) : payouts.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                                            >
                                                Выплаты не найдены
                                            </td>
                                        </tr>
                                    ) : (
                                        payouts.map((p) => {
                                            const isFinalStatus =
                                                p.status_code !== null &&
                                                [2, 4, 6, 8, 9].includes(
                                                    p.status_code,
                                                );
                                            const fee = p.fee
                                                ? `+ ${p.fee} ${p.fee_currency}`
                                                : "";
                                            return (
                                                <tr
                                                    key={p.id}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                                >
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        {p.id}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        <div className="font-medium">
                                                            {p.user?.surname}{" "}
                                                            {p.user?.name}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            ID {p.user_id} ·{" "}
                                                            {p.user?.mobile ||
                                                                "—"}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        {p.amount}{" "}
                                                        {fee && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                ({fee})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        {p.amount_rub ?? "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        {p.rate ?? "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-xs text-gray-900 dark:text-white font-mono">
                                                        {p.wallet_address ??
                                                            "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        <div>
                                                            code:{" "}
                                                            {p.status_code ??
                                                                "—"}
                                                        </div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            {p.status_text ||
                                                                ""}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                        {p.created_at
                                                            ? new Date(
                                                                  p.created_at,
                                                              ).toLocaleString(
                                                                  "ru-RU",
                                                              )
                                                            : "—"}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {!isFinalStatus && (
                                                            <button
                                                                onClick={() =>
                                                                    handleCheckPayout(
                                                                        p.id,
                                                                    )
                                                                }
                                                                disabled={
                                                                    isCheckingPayout
                                                                }
                                                                className="px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                                                            >
                                                                {isCheckingPayout
                                                                    ? "Проверка..."
                                                                    : "Проверить"}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() =>
                                                                setSelectedPayoutId(
                                                                    p.id,
                                                                )
                                                            }
                                                            className="px-3 py-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 border border-blue-300 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50"
                                                        >
                                                            Детали
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {payoutsMeta && (
                            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Показано {payoutsMeta.from ?? 0}–
                                    {payoutsMeta.to ?? 0} из {payoutsMeta.total}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        disabled={payoutsMeta.current_page <= 1}
                                        onClick={() =>
                                            setPayoutsPage((p) =>
                                                Math.max(1, p - 1),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Назад
                                    </button>
                                    <button
                                        disabled={
                                            payoutsMeta.current_page >=
                                            payoutsMeta.last_page
                                        }
                                        onClick={() =>
                                            setPayoutsPage((p) =>
                                                Math.min(
                                                    payoutsMeta.last_page,
                                                    p + 1,
                                                ),
                                            )
                                        }
                                        className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                                    >
                                        Вперёд
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payout Details Modal */}
            {selectedPayoutId !== null && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Детали выплаты #{selectedPayoutId}
                            </h2>
                            <button
                                onClick={() => setSelectedPayoutId(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="overflow-y-auto p-6 flex-1">
                            {isLoadingPayoutDetails ? (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    Загрузка...
                                </div>
                            ) : payoutDetails ? (
                                <div className="space-y-6">
                                    {/* Основная информация */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Основная информация
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    User ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.user_id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    External ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {payoutDetails.external_id}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Altyn ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {payoutDetails.altyn_id ||
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Method
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {
                                                        payoutDetails.payout_method
                                                    }
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Account Number
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {
                                                        payoutDetails.account_number
                                                    }
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    SBP Bank ID
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                    {payoutDetails.sbp_bank_id ||
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Суммы и статус */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Суммы и статус
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Amount RUB
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-semibold">
                                                    {payoutDetails.amount_rub}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Amount USDT
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white font-semibold">
                                                    {payoutDetails.amount ??
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Rate
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.rate ?? "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Status Code
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.status_code ??
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Status Text
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.status_text ||
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Credited Amount USDT
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.amount ??
                                                        "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Комиссия
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.fee ??
                                                        "—"} {payoutDetails.fee_currency ??
                                                        "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Пользователь */}
                                    {payoutDetails.user && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Пользователь
                                            </h3>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Имя
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {
                                                            payoutDetails.user
                                                                .surname
                                                        }{" "}
                                                        {
                                                            payoutDetails.user
                                                                .name
                                                        }
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Email
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {payoutDetails.user
                                                            .email || "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Телефон
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {payoutDetails.user
                                                            .mobile || "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Telegram
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {payoutDetails.user
                                                            .telegram_username ||
                                                            "—"}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                        Страна
                                                    </label>
                                                    <div className="text-sm text-gray-900 dark:text-white">
                                                        {payoutDetails.user
                                                            .country || "—"}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Processing Data */}
                                    {payoutDetails.processing_data && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Processing Data
                                            </h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(
                                                    payoutDetails.processing_data,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Raw Webhook */}
                                    {payoutDetails.raw_response && (
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                                Raw Response
                                            </h3>
                                            <pre className="bg-gray-100 dark:bg-gray-900 p-4 rounded-md text-xs overflow-x-auto">
                                                {JSON.stringify(
                                                    payoutDetails.raw_response,
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Даты */}
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                            Даты
                                        </h3>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Created At
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.created_at
                                                        ? new Date(
                                                              payoutDetails.created_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                                    Updated At
                                                </label>
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {payoutDetails.updated_at
                                                        ? new Date(
                                                              payoutDetails.updated_at,
                                                          ).toLocaleString(
                                                              "ru-RU",
                                                          )
                                                        : "—"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-red-500 dark:text-red-400">
                                    Не удалось загрузить детали платежа
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setSelectedPayoutId(null)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAltynPage;

